import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Observable } from 'rxjs';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { SetDestinationDialogComponent } from '../components/set-destination-dialog/set-destination-dialog.component';
import { HelpDialogComponent } from '../components/help-dialog/help-dialog.component';

export interface DialogConfig {
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  disableClose?: boolean;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  constructor(private dialog: MatDialog) {}

  /**
   * Opens a dialog with the specified component
   */
  openDialog<T, R = any>(
    component: ComponentType<T>, 
    config?: DialogConfig
  ): MatDialogRef<T, R> {
    return this.dialog.open(component, {
      width: config?.width || '400px',
      height: config?.height,
      maxWidth: config?.maxWidth || '90vw',
      maxHeight: config?.maxHeight || '90vh',
      disableClose: config?.disableClose || false,
      data: config?.data,
      autoFocus: true,
      restoreFocus: true
    });
  }

  /**
   * Opens a confirmation dialog
   */
  openConfirmDialog(
    title: string, 
    message: string, 
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel',
    showInstructionsButton?: boolean,
    instructionsData?: { project: any; dataSource: any }
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: showInstructionsButton ? '540px' : '400px',
      data: { 
        title, 
        message, 
        confirmText, 
        cancelText,
        showInstructionsButton,
        instructionsData
      }
    });

    return dialogRef.afterClosed();
  }

  /**
   * Opens the set destination dialog
   */
  openSetDestinationDialog(
    projectId: string, 
    datasourceId?: string, 
    schemaData?: { columns: { name: string; dataType: string }[] } | null,
    existingDestinations?: any[]
  ): Observable<any> {
    const dialogRef = this.dialog.open(SetDestinationDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { 
        title: 'Add Output',
        projectId: projectId,
        datasourceId: datasourceId,
        schemaData: schemaData,
        existingDestinations: existingDestinations,
        mode: 'create'
      }
    });

    return dialogRef.afterClosed();
  }

  /**
   * Opens the destination dialog in view mode to view/delete an existing destination
   */
  openViewDestinationDialog(destination: any): Observable<any> {
    const dialogRef = this.dialog.open(SetDestinationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: {
        title: 'Output',
        projectId: destination.projectId,
        mode: 'view',
        destination: destination
      }
    });

    return dialogRef.afterClosed();
  }

  /**
   * Opens the help dialog
   */
  openHelpDialog(topic?: string, feature?: string): MatDialogRef<HelpDialogComponent> {
    return this.dialog.open(HelpDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      height: '900px',
      maxHeight: '90vh',
      panelClass: 'help-dialog-container',
      data: { topic, feature }
    });
  }

  /**
   * Closes all open dialogs
   */
  closeAll(): void {
    this.dialog.closeAll();
  }
}
